import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  Sun, Battery, Zap, MapPin, Filter, RefreshCw, 
  ChevronDown, Building2, Users, Eye, Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { propertyAPI, installationAPI, customerAPI, geocodeAPI } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Zonnetje-marker in plaats van klassieke pinpoint
const sunIcon = L.divIcon({
  className: 'sun-marker',
  html: `<svg viewBox="0 0 24 24" width="26" height="26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="5" fill="#FBBF24" stroke="#F59E0B" stroke-width="1.5"/>
    <g stroke="#F59E0B" stroke-width="2" stroke-linecap="round">
      <line x1="12" y1="1.5" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22.5"/>
      <line x1="1.5" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22.5" y2="12"/>
      <line x1="4.6" y1="4.6" x2="6.4" y2="6.4"/><line x1="17.6" y1="17.6" x2="19.4" y2="19.4"/>
      <line x1="4.6" y1="19.4" x2="6.4" y2="17.6"/><line x1="17.6" y1="6.4" x2="19.4" y2="4.6"/>
    </g>
  </svg>`,
  iconSize: [26, 26],
  iconAnchor: [13, 13],
  popupAnchor: [0, -14],
});

// Component to fit map bounds
const FitBounds = ({ locations }) => {
  const map = useMap();
  
  useEffect(() => {
    if (locations && locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(loc => [loc.lat, loc.lng]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map]);
  
  return null;
};

const InstallationMap = ({ embedded = false }) => {
  const [properties, setProperties] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geocodedProperties, setGeocodedProperties] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState({ done: 0, total: 0 });
  const navigate = useNavigate();

  // Filters
  const [filters, setFilters] = useState({
    showSolar: true,
    showBattery: true,
    showEVCharger: true,
    minPanels: 0,
    city: ''
  });

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [propsRes, instsRes, custsRes] = await Promise.all([
          propertyAPI.getAll(),
          installationAPI.getAll(),
          customerAPI.getAll()
        ]);
        setProperties(propsRes.data);
        setInstallations(instsRes.data);
        setCustomers(custsRes.data);
      } catch (error) {
        toast.error('Kon data niet laden');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Use cached coordinates from properties, fallback to city-based estimation
  useEffect(() => {
    if (properties.length === 0) return;

    // Belgian city coordinates (approximate) for fallback
    const belgianCities = {
      'Brussel': { lat: 50.8503, lng: 4.3517 },
      'Antwerpen': { lat: 51.2194, lng: 4.4025 },
      'Gent': { lat: 51.0543, lng: 3.7174 },
      'Brugge': { lat: 51.2093, lng: 3.2247 },
      'Leuven': { lat: 50.8798, lng: 4.7005 },
      'Mechelen': { lat: 51.0259, lng: 4.4776 },
      'Aalst': { lat: 50.9364, lng: 4.0355 },
      'Hasselt': { lat: 50.9307, lng: 5.3378 },
      'Kortrijk': { lat: 50.8279, lng: 3.2654 },
      'Oostende': { lat: 51.2154, lng: 2.9286 },
      'Charleroi': { lat: 50.4108, lng: 4.4446 },
      'Luik': { lat: 50.6326, lng: 5.5797 },
      'Namen': { lat: 50.4674, lng: 4.8720 },
      'Mons': { lat: 50.4542, lng: 3.9523 },
      'Dendermonde': { lat: 51.0284, lng: 4.1019 },
      'Hamme': { lat: 51.0967, lng: 4.1342 },
      'Baasrode': { lat: 51.0458, lng: 4.1583 },
      'Lokeren': { lat: 51.1039, lng: 3.9903 },
      'Sint-Niklaas': { lat: 51.1564, lng: 4.1433 },
      'Temse': { lat: 51.1286, lng: 4.2131 },
      'Zele': { lat: 51.0667, lng: 4.0333 },
      'default': { lat: 50.85, lng: 4.35 }
    };

    const geocoded = properties.map((prop) => {
      // If property already has coordinates (from geocoding), use them — maar enkel binnen België
      const latN = parseFloat(prop.gps_lat);
      const lngN = parseFloat(prop.gps_lng);
      const inBelgium = latN >= 49.4 && latN <= 51.7 && lngN >= 2.3 && lngN <= 6.5;
      if (prop.gps_lat && prop.gps_lng && inBelgium) {
        return {
          ...prop,
          lat: latN,
          lng: lngN,
          geocoded: true
        };
      }
      
      // Otherwise, estimate based on city with small random offset
      let baseCoords = belgianCities.default;
      
      const cityLower = (prop.city || '').toLowerCase();
      for (const [city, coords] of Object.entries(belgianCities)) {
        if (cityLower.includes(city.toLowerCase()) || city.toLowerCase().includes(cityLower)) {
          baseCoords = coords;
          break;
        }
      }
      
      // Small random offset to prevent markers stacking
      const lat = baseCoords.lat + (Math.random() - 0.5) * 0.02;
      const lng = baseCoords.lng + (Math.random() - 0.5) * 0.03;
      
      return {
        ...prop,
        lat,
        lng,
        geocoded: false
      };
    });

    setGeocodedProperties(geocoded);
  }, [properties]);

  // Calculate stats per property
  const propertyStats = useMemo(() => {
    const stats = {};
    
    geocodedProperties.forEach(prop => {
      const propInstallations = installations.filter(i => i.property_id === prop.id);
      const customer = customers.find(c => c.id === prop.customer_id);
      
      let totalPanels = 0;
      let totalKwp = 0;
      let hasBattery = false;
      let hasEVCharger = false;
      let hasSolar = false;
      let batteryCapacity = '';
      
      propInstallations.forEach(inst => {
        if (inst.installation_type === 'solar_panels') {
          hasSolar = true;
          totalPanels += inst.number_of_panels || 0;
          if (inst.number_of_panels && inst.panel_wattage) {
            totalKwp += (inst.number_of_panels * inst.panel_wattage) / 1000;
          }
        }
        if (inst.installation_type === 'battery') {
          hasBattery = true;
          batteryCapacity = inst.battery_capacity || '';
        }
        if (inst.installation_type === 'ev_charger') {
          hasEVCharger = true;
        }
      });

      // Determine icon type
      let iconType = 'default';
      const typeCount = [hasSolar, hasBattery, hasEVCharger].filter(Boolean).length;
      if (typeCount > 1) {
        iconType = 'mixed';
      } else if (hasSolar) {
        iconType = 'solar';
      } else if (hasBattery) {
        iconType = 'battery';
      } else if (hasEVCharger) {
        iconType = 'ev_charger';
      }
      
      stats[prop.id] = {
        property: prop,
        customer,
        totalPanels,
        totalKwp: totalKwp.toFixed(2),
        hasBattery,
        hasEVCharger,
        hasSolar,
        batteryCapacity,
        iconType,
        installations: propInstallations
      };
    });
    
    return stats;
  }, [geocodedProperties, installations, customers]);

  // Filter properties
  const filteredProperties = useMemo(() => {
    return geocodedProperties.filter(prop => {
      const stats = propertyStats[prop.id];
      if (!stats) return false;
      
      // Installation type filters
      if (!filters.showSolar && stats.hasSolar && !stats.hasBattery && !stats.hasEVCharger) return false;
      if (!filters.showBattery && stats.hasBattery && !stats.hasSolar && !stats.hasEVCharger) return false;
      if (!filters.showEVCharger && stats.hasEVCharger && !stats.hasSolar && !stats.hasBattery) return false;
      
      // Panel count filter
      if (filters.minPanels > 0 && stats.totalPanels < filters.minPanels) return false;
      
      // City filter
      if (filters.city && !prop.city?.toLowerCase().includes(filters.city.toLowerCase())) return false;
      
      return true;
    });
  }, [geocodedProperties, propertyStats, filters]);

  // Calculate totals
  const totals = useMemo(() => {
    let totalPanels = 0;
    let totalKwp = 0;
    let solarCount = 0;
    let batteryCount = 0;
    let evChargerCount = 0;
    
    filteredProperties.forEach(prop => {
      const stats = propertyStats[prop.id];
      if (stats) {
        totalPanels += stats.totalPanels;
        totalKwp += parseFloat(stats.totalKwp);
        if (stats.hasSolar) solarCount++;
        if (stats.hasBattery) batteryCount++;
        if (stats.hasEVCharger) evChargerCount++;
      }
    });
    
    return { totalPanels, totalKwp: totalKwp.toFixed(2), solarCount, batteryCount, evChargerCount };
  }, [filteredProperties, propertyStats]);

  // Get unique cities for filter
  const uniqueCities = useMemo(() => {
    return [...new Set(properties.map(p => p.city).filter(Boolean))].sort();
  }, [properties]);

  // Count geocoded vs not geocoded
  const geocodeStats = useMemo(() => {
    const geocoded = geocodedProperties.filter(p => p.geocoded).length;
    const notGeocoded = geocodedProperties.filter(p => !p.geocoded).length;
    return { geocoded, notGeocoded };
  }, [geocodedProperties]);

  // Geocode ungecoded properties (achtergrondjob op de server)
  const handleGeocodeAll = async () => {
    const notGeocoded = geocodedProperties.filter(p => !p.geocoded).length;
    if (notGeocoded === 0) {
      toast.info('Alle adressen zijn al gegeocodeerd');
      return;
    }
    
    setGeocoding(true);
    try {
      await geocodeAPI.verifyAll();
      toast.success('Adresverificatie gestart — dit loopt automatisch op de achtergrond');
      const poll = setInterval(async () => {
        try {
          const { data } = await geocodeAPI.verifyStatus();
          setGeocodeProgress({ done: data.done || 0, total: data.total || 0 });
          if (!data.running) {
            clearInterval(poll);
            setGeocoding(false);
            toast.success(`Klaar: ${data.success} adressen geplaatst${data.failed ? `, ${data.failed} niet gevonden` : ''}`);
            const propsRes = await propertyAPI.getAll();
            setProperties(propsRes.data);
          }
        } catch (e) {
          // netwerkhapering — blijf pollen
        }
      }, 4000);
    } catch (error) {
      toast.error('Kon adresverificatie niet starten');
      setGeocoding(false);
    }
  };

  const mapCenter = [50.85, 4.35]; // Belgium center

  return (
    <div className={embedded ? "space-y-4" : "space-y-4 animate-fade-in h-[calc(100vh-120px)]"} data-testid="map-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          {embedded ? (
            <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2">
              <Sun className="w-5 h-5 text-amber-400" />
              Installatiekaart
            </h2>
          ) : (
            <h1 className="text-2xl md:text-4xl font-bold tracking-tight">Kaart</h1>
          )}
          <p className="text-muted-foreground text-sm mt-1">
            {filteredProperties.length} locaties met installaties
            {geocodeStats.notGeocoded > 0 && (
              <span className="text-amber-500 ml-2">
                ({geocodeStats.notGeocoded} niet exact geplaatst)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {geocodeStats.notGeocoded > 0 && (
            <Button 
              variant="outline" 
              className="gap-2"
              onClick={handleGeocodeAll}
              disabled={geocoding}
            >
              <MapPin className="w-4 h-4" />
              {geocoding ? `Geocoding... (${geocodeProgress.done}/${geocodeProgress.total})` : `Adressen verifiëren (${geocodeStats.notGeocoded})`}
            </Button>
          )}
          <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Filter className="w-4 h-4" />
                Filters
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Kaart Filters</SheetTitle>
                <SheetDescription>Filter installaties op de kaart</SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-6">
                <div className="space-y-3">
                  <label className="text-sm font-medium">Installatie Types</label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="solar" 
                        checked={filters.showSolar}
                        onCheckedChange={(checked) => setFilters(prev => ({ ...prev, showSolar: checked }))}
                      />
                      <label htmlFor="solar" className="text-sm flex items-center gap-2 cursor-pointer">
                        <Sun className="w-4 h-4 text-amber-400" />
                        Zonnepanelen
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="battery" 
                        checked={filters.showBattery}
                        onCheckedChange={(checked) => setFilters(prev => ({ ...prev, showBattery: checked }))}
                      />
                      <label htmlFor="battery" className="text-sm flex items-center gap-2 cursor-pointer">
                        <Battery className="w-4 h-4 text-green-400" />
                        Batterij
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="ev" 
                        checked={filters.showEVCharger}
                        onCheckedChange={(checked) => setFilters(prev => ({ ...prev, showEVCharger: checked }))}
                      />
                      <label htmlFor="ev" className="text-sm flex items-center gap-2 cursor-pointer">
                        <Zap className="w-4 h-4 text-blue-400" />
                        Laadpaal
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <label className="text-sm font-medium">Minimum panelen</label>
                  <Select 
                    value={String(filters.minPanels)} 
                    onValueChange={(v) => setFilters(prev => ({ ...prev, minPanels: parseInt(v) }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Alle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Alle</SelectItem>
                      <SelectItem value="10">10+ panelen</SelectItem>
                      <SelectItem value="20">20+ panelen</SelectItem>
                      <SelectItem value="30">30+ panelen</SelectItem>
                      <SelectItem value="50">50+ panelen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium">Gemeente</label>
                  <Select 
                    value={filters.city} 
                    onValueChange={(v) => setFilters(prev => ({ ...prev, city: v === 'all' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Alle gemeenten" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="all">Alle gemeenten</SelectItem>
                      {uniqueCities.slice(0, 50).map(city => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setFilters({ showSolar: true, showBattery: true, showEVCharger: true, minPanels: 0, city: '' })}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset Filters
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Stats Cards */}
      {!embedded && (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-3 flex items-center gap-2">
            <Sun className="w-5 h-5 text-amber-400" />
            <div>
              <p className="text-lg font-bold">{totals.totalPanels.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Panelen</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-orange-500/10 border-orange-500/30">
          <CardContent className="p-3 flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-400" />
            <div>
              <p className="text-lg font-bold">{totals.totalKwp}</p>
              <p className="text-xs text-muted-foreground">kWp totaal</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <CardContent className="p-3 flex items-center gap-2">
            <Sun className="w-5 h-5 text-yellow-400" />
            <div>
              <p className="text-lg font-bold">{totals.solarCount}</p>
              <p className="text-xs text-muted-foreground">Zonnepanelen</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-3 flex items-center gap-2">
            <Battery className="w-5 h-5 text-green-400" />
            <div>
              <p className="text-lg font-bold">{totals.batteryCount}</p>
              <p className="text-xs text-muted-foreground">Batterijen</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="p-3 flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-400" />
            <div>
              <p className="text-lg font-bold">{totals.evChargerCount}</p>
              <p className="text-xs text-muted-foreground">Laadpalen</p>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Simple Legend */}
      {!embedded && (
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span className="text-muted-foreground">Legenda:</span>
        <div className="flex items-center gap-1">
          <Sun className="w-4 h-4 text-amber-400" />
          <span>Adres met installatie</span>
        </div>
      </div>
      )}

      {/* Map */}
      <div className="flex-1 rounded-xl overflow-hidden border border-border shadow-lg" style={{ height: embedded ? '480px' : 'calc(100vh - 380px)', minHeight: '400px' }}>
        {loading ? (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">Kaart laden...</p>
            </div>
          </div>
        ) : (
          <MapContainer 
            center={mapCenter} 
            zoom={8} 
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds locations={filteredProperties.map(p => ({ lat: p.lat, lng: p.lng }))} />
            
            {filteredProperties.map(prop => {
              const stats = propertyStats[prop.id];
              if (!stats || !prop.lat || !prop.lng) return null;
              
              return (
                <Marker 
                  key={prop.id} 
                  position={[prop.lat, prop.lng]}
                  icon={sunIcon}
                >
                  <Popup className="custom-popup" minWidth={280}>
                    <div className="p-1">
                      <h3 className="font-bold text-base mb-1">
                        {prop.street} {prop.number}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        {prop.postal_code} {prop.city}
                      </p>
                      
                      {stats.customer && (
                        <p className="text-sm mb-2 flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {stats.customer.first_name} {stats.customer.last_name}
                        </p>
                      )}
                      
                      <div className="border-t pt-2 mt-2 space-y-1">
                        {stats.hasSolar && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-1">
                              <Sun className="w-4 h-4 text-amber-500" />
                              Zonnepanelen
                            </span>
                            <span className="font-medium">
                              {stats.totalPanels} stuks ({stats.totalKwp} kWp)
                            </span>
                          </div>
                        )}
                        {stats.hasBattery && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-1">
                              <Battery className="w-4 h-4 text-green-500" />
                              Batterij
                            </span>
                            <span className="font-medium">
                              {stats.batteryCapacity || 'Ja'}
                            </span>
                          </div>
                        )}
                        {stats.hasEVCharger && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-1">
                              <Zap className="w-4 h-4 text-blue-500" />
                              Laadpaal
                            </span>
                            <span className="font-medium">Ja</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-3 pt-2 border-t flex gap-2">
                        <button 
                          onClick={() => navigate(`/properties/${prop.id}`)}
                          className="flex-1 text-sm bg-primary text-primary-foreground py-1.5 px-3 rounded hover:bg-primary/90 flex items-center justify-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          Details
                        </button>
                        <button 
                          onClick={() => navigate(`/agenda?property=${prop.id}&type=cleaning`)}
                          className="flex-1 text-sm bg-orange-500 text-white py-1.5 px-3 rounded hover:bg-orange-600 flex items-center justify-center gap-1"
                        >
                          <Sparkles className="w-4 h-4" />
                          Reiniging
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}
      </div>
    </div>
  );
};

export default InstallationMap;
